import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchAllUsers,
  deleteUser,
  updateUserInfo,
  updateUserStatus,
  resetPassword
} from '../store/slices/authSlice'
import { fetchSocieties } from '../store/slices/societySlice'
import Switcher3 from '../components/Switcher3'
import {
  Phone,
  Mail,
  UserPlus,
  Edit2,
  Trash2,
  Search,
  User,
  IdCard,
  Upload,
  CalendarClock,
  Users,
  Building,
  EyeOff,
  Eye,
  XCircle,
  Lock
} from 'lucide-react'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'

const EmployeeTable = () => {
  const dispatch = useDispatch()
  const { users, activeUser } = useSelector(state => state.auth)
  const { activeSociety, gradient, theme } = useSidebarContext()
  const [searchQuery, setSearchQuery] = useState('')
  const [isModalOpen, setModalOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [updatedUsers, setUpdatedUsers] = useState([])
  const [previewImage, setPreviewImage] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [isResetModalOpen, setResetModalOpen] = useState(false)
  const [selectedUserForReset, setSelectedUserForReset] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const isEmployee = activeUser?.role === 'Employee'

  useEffect(() => {
    dispatch(fetchAllUsers())
    dispatch(fetchSocieties())
  }, [dispatch])

  useEffect(() => {
    setUpdatedUsers(users)
  }, [users, activeSociety])

  const openResetPasswordModal = user => {
    setSelectedUserForReset(user)
    setNewPassword('')
    setConfirmPassword('')
    setShowNewPassword(false)
    setShowConfirmPassword(false)
    setResetModalOpen(true)
  }

  const handleResetPassword = () => {
    if (!selectedUserForReset) {
      alert('No user selected for password reset!')
      return
    }

    if (newPassword !== confirmPassword) {
      alert('Passwords do not match!')
      return
    }

    dispatch(
      resetPassword({
        userId: selectedUserForReset._id,
        newPassword
      })
    ).then(res => {
      if (res.meta.requestStatus === 'fulfilled') {
        alert('Password reset successfully!')
        setResetModalOpen(false)
        setSelectedUserForReset(null)
      } else {
        alert('Password reset failed!')
      }
    })
  }

  const getPasswordStrength = password => {
    if (password.length < 6) return 'Weak'
    if (password.length < 10) return 'Medium'
    return 'Strong'
  }

  const cleanProfilePictureURL = url => {
    if (!url) return null
    if (url.includes('//uploads')) {
      return url.replace('//uploads', '/uploads')
    }

    return url
  }

  const filteredUsers = updatedUsers
    .filter(user => {
      const isEmployee = user.role === 'Employee'
      const isSocietyMatch =
        activeSociety &&
        user.societies &&
        Array.isArray(user.societies) &&
        user.societies.some(society => society._id === activeSociety._id)
      const showAllEmployees = !activeSociety && isEmployee

      const isStatusValid =
        !user.status || user.status === 'active' || user.status === 'inactive'

      return isEmployee && isStatusValid && (isSocietyMatch || showAllEmployees)
    })

    .filter(user => {
      if (!searchQuery) return true

      const employeeData = user.employeeData || {}
      const personalDetails = employeeData.personalDetails || {}

      const name = employeeData.name || ''
      const designation = employeeData.designation || ''
      const phone = personalDetails.phone || ''
      const address = personalDetails.address || ''
      const dateOfJoining = personalDetails.dateOfJoining || ''
      const username = user.username || ''

      const matchesQuery = [
        name,
        designation,
        phone,
        address,
        dateOfJoining,
        username
      ].some(field =>
        field.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )

      return matchesQuery
    })

  const handleEdit = user => {
    if (isEmployee) return
    setSelectedEmployee(user)
    setModalOpen(true)
  }

  const handleDelete = userId => {
    if (isEmployee) return
    if (window.confirm('Are you sure you want to delete this Employee?')) {
      // console.log("Deleting user with ID:", userId);
      dispatch(deleteUser(userId))
      setUpdatedUsers(prevUsers =>
        prevUsers.filter(user => user._id !== userId)
      )
      dispatch(fetchAllUsers())
    }
  }

  const handleImageChange = e => {
    const file = e.target.files[0]
    if (file) {
      setIsUploading(true)

      setSelectedEmployee(prev => ({
        ...prev,
        profile_picture_file: file,
        profile_picture: URL.createObjectURL(file)
      }))
      setPreviewImage(URL.createObjectURL(file))
      setIsUploading(false)
    }
  }

  const handleInputChange = (e, field) => {
    const { value } = e.target
    setSelectedEmployee(prevSelectedEmployee => ({
      ...prevSelectedEmployee,
      employeeData: {
        ...prevSelectedEmployee.employeeData,
        personalDetails: {
          ...prevSelectedEmployee.employeeData.personalDetails,
          [field]: value
        },
        salaryDetails: {
          ...prevSelectedEmployee.employeeData.salaryDetails,
          [field]: value
        }
      }
    }))
  }

  const handleUpdate = () => {
    if (!selectedEmployee?._id) {
      console.error('User ID is missing, cannot update.')
      return
    }

    const formData = new FormData()

    formData.append('username', selectedEmployee.username)
    formData.append('role', 'Employee')

    if (selectedEmployee.societies) {
      formData.append('societies', JSON.stringify(selectedEmployee.societies))
    }
    if (selectedEmployee.status) {
      formData.append('status', selectedEmployee.status)
    }
    if (selectedEmployee.profile_picture_file) {
      formData.append('profile_picture', selectedEmployee.profile_picture_file)
    }

    if (selectedEmployee.employeeData) {
      formData.append(
        'employeeData[name]',
        selectedEmployee.employeeData.name || ''
      )
      formData.append(
        'employeeData[designation]',
        selectedEmployee.employeeData.designation || ''
      )

      if (selectedEmployee.employeeData.personalDetails) {
        const personalDetails = selectedEmployee.employeeData.personalDetails

        formData.append(
          'employeeData[personalDetails][phone]',
          personalDetails.phone || ''
        )
        formData.append(
          'employeeData[personalDetails][address]',
          personalDetails.address || ''
        )
        formData.append(
          'employeeData[personalDetails][cnic]',
          personalDetails.cnic || ''
        )
        formData.append(
          'employeeData[personalDetails][dateOfJoining]',
          personalDetails.dateOfJoining || ''
        )
      }
      if (selectedEmployee.employeeData.salaryDetails) {
        const salaryDetails = selectedEmployee.employeeData.salaryDetails

        formData.append(
          'employeeData[salaryDetails][salary]',
          salaryDetails.salary || ''
        )
      }
    }

    // console.log("Sending updated data as FormData");

    dispatch(
      updateUserInfo({ userId: selectedEmployee._id, updatedData: formData })
    )
      .then(res => {
        // console.log("Update response:", res);

        if (res.meta.requestStatus === 'fulfilled') {
          // console.log("Update successful, fetching users...");
          dispatch(fetchAllUsers()).then(res => {
            setUpdatedUsers(res.payload)
          })
          setModalOpen(false)
        } else {
          console.error('Update failed:', res.error)
        }
      })
      .catch(error => {
        console.error('Error updating user:', error)
      })
  }

  const handleStatusChange = (userId, newStatus) => {
    const updatedData = { status: newStatus }
    // console.log("Sending status update:", { userId, updatedData });
    if (isEmployee) return
    dispatch(updateUserStatus({ userId, status: newStatus }))
      .then(res => {
        if (res.meta.requestStatus === 'fulfilled') {
          dispatch(fetchAllUsers())
          // console.log("Status updated successfully for user ID:", userId);
        } else {
          console.error('Status update failed:', res.error)
        }
      })
      .catch(error => {
        console.error('Error updating user status:', error)
      })
  }

  function formatCNIC (cnic) {
    if (!cnic) return ''
    cnic = cnic.replace(/\D/g, '')
    cnic = cnic.slice(0, 13)
    return cnic.replace(/^(\d{5})(\d{7})(\d{1})$/, '$1-$2-$3')
  }

  return (
    <div
      className={`${gradient} rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] p-8 animate-gradient-x`}
      style={{
        backgroundImage: `
        linear-gradient(to bottom right, ${theme.mui.bg2}),
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Crect fill='%231A1F2E' width='540' height='450'/%3E%3Cdefs%3E%3ClinearGradient id='a' gradientUnits='userSpaceOnUse' x1='0' x2='0' y1='0' y2='100%25' gradientTransform='rotate(240)'%3E%3Cstop offset='0' stop-color='%231A1F2E'/%3E%3Cstop offset='1' stop-color='%23373F5A'/%3E%3C/linearGradient%3E%3Cpattern patternUnits='userSpaceOnUse' id='b' width='300' height='250' x='0' y='0' viewBox='0 0 1080 900'%3E%3Cg fill-opacity='0.04'%3E%3Cpolygon fill='%23444' points='90 150 0 300 180 300'/%3E%3Cpolygon points='90 150 180 0 0 0'/%3E%3Cpolygon fill='%23AAA' points='270 150 360 0 180 0'/%3E%3Cpolygon fill='%23DDD' points='450 150 360 300 540 300'/%3E%3Cpolygon fill='%23999' points='450 150 540 0 360 0'/%3E%3Cpolygon points='630 150 540 300 720 300'/%3E%3Cpolygon fill='%23DDD' points='630 150 720 0 540 0'/%3E%3Cpolygon fill='%23444' points='810 150 720 300 900 300'/%3E%3Cpolygon fill='%23FFF' points='810 150 900 0 720 0'/%3E%3Cpolygon fill='%23DDD' points='990 150 900 300 1080 300'/%3E%3Cpolygon fill='%23444' points='990 150 1080 0 900 0'/%3E%3Cpolygon fill='%23DDD' points='90 450 0 600 180 600'/%3E%3Cpolygon points='90 450 180 300 0 300'/%3E%3Cpolygon fill='%23666' points='270 450 180 600 360 600'/%3E%3Cpolygon fill='%23AAA' points='270 450 360 300 180 300'/%3E%3Cpolygon fill='%23DDD' points='450 450 360 600 540 600'/%3E%3Cpolygon fill='%23999' points='450 450 540 300 360 300'/%3E%3Cpolygon fill='%23999' points='630 450 540 600 720 600'/%3E%3Cpolygon fill='%23FFF' points='630 450 720 300 540 300'/%3E%3Cpolygon points='810 450 720 600 900 600'/%3E%3Cpolygon fill='%23DDD' points='810 450 900 300 720 300'/%3E%3Cpolygon fill='%23AAA' points='990 450 900 600 1080 600'/%3E%3Cpolygon fill='%23444' points='990 450 1080 300 900 300'/%3E%3Cpolygon fill='%23222' points='90 750 0 900 180 900'/%3E%3Cpolygon points='270 750 180 900 360 900'/%3E%3Cpolygon fill='%23DDD' points='270 750 360 600 180 600'/%3E%3Cpolygon points='450 750 540 600 360 600'/%3E%3Cpolygon points='630 750 540 900 720 900'/%3E%3Cpolygon fill='%23444' points='630 750 720 600 540 600'/%3E%3Cpolygon fill='%23AAA' points='810 750 720 900 900 900'/%3E%3Cpolygon fill='%23666' points='810 750 900 600 720 600'/%3E%3Cpolygon fill='%23999' points='990 750 900 900 1080 900'/%3E%3Cpolygon fill='%23999' points='180 0 90 150 270 150'/%3E%3Cpolygon fill='%23444' points='360 0 270 150 450 150'/%3E%3Cpolygon fill='%23FFF' points='540 0 450 150 630 150'/%3E%3Cpolygon points='900 0 810 150 990 150'/%3E%3Cpolygon fill='%23222' points='0 300 -90 450 90 450'/%3E%3Cpolygon fill='%23FFF' points='0 300 90 150 -90 150'/%3E%3Cpolygon fill='%23FFF' points='180 300 90 450 270 450'/%3E%3Cpolygon fill='%23666' points='180 300 270 150 90 150'/%3E%3Cpolygon fill='%23222' points='360 300 270 450 450 450'/%3E%3Cpolygon fill='%23FFF' points='360 300 450 150 270 150'/%3E%3Cpolygon fill='%23444' points='540 300 450 450 630 450'/%3E%3Cpolygon fill='%23222' points='540 300 630 150 450 150'/%3E%3Cpolygon fill='%23AAA' points='720 300 630 450 810 450'/%3E%3Cpolygon fill='%23666' points='720 300 810 150 630 150'/%3E%3Cpolygon fill='%23FFF' points='900 300 810 450 990 450'/%3E%3Cpolygon fill='%23999' points='900 300 990 150 810 150'/%3E%3Cpolygon points='0 600 -90 750 90 750'/%3E%3Cpolygon fill='%23666' points='0 600 90 450 -90 450'/%3E%3Cpolygon fill='%23AAA' points='180 600 90 750 270 750'/%3E%3Cpolygon fill='%23444' points='180 600 270 450 90 450'/%3E%3Cpolygon fill='%23444' points='360 600 270 750 450 750'/%3E%3Cpolygon fill='%23999' points='360 600 450 450 270 450'/%3E%3Cpolygon fill='%23666' points='540 600 630 450 450 450'/%3E%3Cpolygon fill='%23222' points='720 600 630 750 810 750'/%3E%3Cpolygon fill='%23FFF' points='900 600 810 750 990 750'/%3E%3Cpolygon fill='%23222' points='900 600 990 450 810 450'/%3E%3Cpolygon fill='%23DDD' points='0 900 90 750 -90 750'/%3E%3Cpolygon fill='%23444' points='180 900 270 750 90 750'/%3E%3Cpolygon fill='%23FFF' points='360 900 450 750 270 750'/%3E%3Cpolygon fill='%23AAA' points='540 900 630 750 450 750'/%3E%3Cpolygon fill='%23FFF' points='720 900 810 750 630 750'/%3E%3Cpolygon fill='%23222' points='900 900 990 750 810 750'/%3E%3Cpolygon fill='%23222' points='1080 300 990 450 1170 450'/%3E%3Cpolygon fill='%23FFF' points='1080 300 1170 150 990 150'/%3E%3Cpolygon points='1080 600 990 750 1170 750'/%3E%3Cpolygon fill='%23666' points='1080 600 1170 450 990 450'/%3E%3Cpolygon fill='%23DDD' points='1080 900 1170 750 990 750'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect x='0' y='0' fill='url(%23a)' width='100%25' height='100%25'/%3E%3Crect x='0' y='0' fill='url(%23b)' width='100%25' height='100%25'/%3E%3C/svg%3E")
      `
      }}
    >
      <div className='max-w-7xl mx-auto space-y-8'>
        {/* Enhanced Header Section */}
        <div className='relative group p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/20 transition-all duration-500'>
          <div className='flex flex-col space-y-3'>
            <h1
              className={`text-4xl font-bold tracking-tight ${theme.tailwind.bgr6} text-transparent bg-clip-text transform group-hover:scale-[1.02] transition-all duration-500`}
            >
              Employee Management
            </h1>
            <div
              className={`flex items-center space-x-2 ${theme.tailwind.text4}`}
            >
              <Building className='h-5 w-5' />
              <span>{activeSociety?.name || 'No Society Selected'}</span>
            </div>
            <div className='flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-xl'>
              <Users className={`h-5 w-5 ${theme.tailwind.text9}`} />
              <span className={`${theme.tailwind.text9} font-medium`}>
                {filteredUsers.length} Employees
              </span>
            </div>
            <p
              className={`${theme.tailwind.text4} text-lg transform group-hover:translate-x-2 transition-all duration-500`}
            >
              Manage and monitor your employee base efficiently
            </p>
          </div>
          <div
            className={`absolute inset-0 rounded-2xl ${theme.tailwind.bgr20} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
          />
        </div>

        {/* Enhanced Search Bar */}
        <div className='relative group transform hover:-translate-y-1 transition-all duration-500'>
          <div
            className={`absolute inset-0 rounded-xl ${theme.tailwind.bgr21} blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500`}
          />
          <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
            <Search
              className={`h-6 w-6 ${theme.tailwind.text9} group-hover:rotate-12 transition-transform duration-500`}
            />
          </div>
          <input
            type='text'
            placeholder='Search users by name, email, or CNIC...'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={`w-full pl-12 pr-4 py-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
            ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
            ${theme.tailwind.focus7} transition-all duration-500 backdrop-blur-xl
            hover:bg-white/10 hover:shadow-lg ${theme.tailwind.shadowhover6}`}
          />
        </div>

        <div className='relative group'>
          <div className='absolute inset-0 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500' />
          <div
            className={`relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 
              hover:border-white/20 transition-all duration-500 hover:shadow-xl ${theme.tailwind.shadowhover6}`}
          >
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-white/10 bg-white/5'>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      User Details
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      Society
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      Personal Details
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      salary
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      Status
                    </th>
                    {!isEmployee && (
                      <th
                        className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                      >
                        Actions
                      </th>
                    )}
                    {!isEmployee && (
                      <th
                        className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                      >
                        Reset Password
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map(user => (
                      <tr
                        key={user._id}
                        onMouseEnter={() => setHoveredRow(user._id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        className='group/row border-b border-white/5 hover:bg-white/10 transition-all duration-500 cursor-pointer'
                      >
                        <td className='px-6 py-4'>
                          <div className='flex items-center space-x-4'>
                            <div className='relative group/avatar'>
                              {user.profile_picture ? (
                                <img
                                  src={cleanProfilePictureURL(
                                    user.profile_picture
                                  )}
                                  alt={user.employeeData?.name}
                                  className={`w-12 h-12 rounded-full object-cover border-2 ${theme.tailwind.border1} 
                                group-hover/avatar:scale-110 transition-all duration-500`}
                                />
                              ) : (
                                <div
                                  className={`w-12 h-12 rounded-full ${theme.tailwind.bg11} flex items-center justify-center 
                                border-2 ${theme.tailwind.border1} group-hover/avatar:scale-110 transition-all duration-500`}
                                >
                                  <User
                                    className={`h-6 w-6 ${theme.tailwind.text9}`}
                                  />
                                </div>
                              )}
                              <div
                                className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${theme.tailwind.bg40} border-2 ${theme.tailwind.bg55}
                              group-hover/avatar:scale-125 transition-all duration-500`}
                              />
                            </div>
                            <div>
                              <div
                                className={`${theme.tailwind.text9} font-medium group-hover/row:translate-x-2 transition-transform duration-500`}
                              >
                                {user.employeeData?.name || 'N/A'}
                              </div>
                              <div
                                className={`${theme.tailwind.text20} text-sm group-hover/row:translate-x-2 transition-transform duration-500 delay-75`}
                              >
                                {user.username}
                              </div>
                              {!isEmployee && (
                                <div
                                  className={`inline-flex items-center mt-2 gap-2 px-2 py-1 rounded-full ${theme.tailwind.bgGradient18} ring-2 ${theme.tailwind.ringColor5} group hover:scale-105 transition-all duration-500`}
                                >
                                  <div className='flex items-center gap-2'>
                                    <span className='mt-1.5'>******</span>
                                    <Lock
                                      className={`w-4 h-4 ${theme.tailwind.text18}`}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className={`px-6 py-4 ${theme.tailwind.text9}`}>
                          {user.societies && Array.isArray(user.societies)
                            ? user.societies
                                .map(society => society.name)
                                .join(', ')
                            : 'N/A'}
                        </td>

                        <td className='px-6 py-4'>
                          <div className='flex flex-col space-y-2'>
                            <div
                              className={`flex items-center space-x-2 ${theme.tailwind.text9}`}
                            >
                              <Phone className='h-4 w-4' />
                              <span>
                                {user.employeeData?.personalDetails?.phone ||
                                  'N/A'}
                              </span>
                            </div>
                            <div
                              className={`flex items-center space-x-2 ${theme.tailwind.text20}`}
                            >
                              <Mail className='h-4 w-4' />
                              <span>
                                {user.employeeData?.personalDetails?.address ||
                                  'N/A'}
                              </span>
                            </div>

                            <div
                              className={`flex items-center space-x-2 ${theme.tailwind.text20}`}
                            >
                              <IdCard className='h-4 w-4' />
                              <span>
                                {user.employeeData?.personalDetails?.cnic
                                  ? formatCNIC(
                                      user.employeeData.personalDetails.cnic
                                    )
                                  : 'N/A'}
                              </span>
                            </div>
                            <div
                              className={`flex items-center space-x-2 ${theme.tailwind.text20}`}
                            >
                              <CalendarClock className='h-4 w-4' />
                              <span>
                                {new Date(
                                  user.employeeData.personalDetails.dateOfJoining
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className='px-6 py-3'>
                          <div
                            className={`flex items-center space-x-2 ${theme.tailwind.text9}`}
                          >
                            Rs.
                            {user.employeeData?.salaryDetails?.salary
                              ? Number(
                                  user.employeeData.salaryDetails.salary
                                ).toLocaleString()
                              : 'N/A'}
                          </div>
                        </td>
                        <td className='px-6 py-4'>
                          {isEmployee ? (
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium ${
                                user.status === 'active'
                                  ? 'bg-green-400/10 text-green-400'
                                  : 'bg-red-400/10 text-red-400'
                              }`}
                            >
                              {user.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          ) : (
                            <Switcher3
                              isChecked={user.status === 'active'}
                              onToggle={newStatus =>
                                handleStatusChange(user._id, newStatus)
                              }
                            />
                          )}
                        </td>
                        {!isEmployee && (
                          <td className='px-6 py-4'>
                            <div className='flex space-x-3'>
                              <button
                                onClick={() => handleEdit(user)}
                                className={`p-2 rounded-lg ${theme.tailwind.bg11} ${theme.tailwind.bgHover26} ${theme.tailwind.text9} 
                          transform hover:scale-110 transition-all duration-300`}
                              >
                                <Edit2 className='h-4 w-4' />
                              </button>
                              <button
                                onClick={() => handleDelete(user._id)}
                                className='p-2 rounded-lg bg-red-400/10 hover:bg-red-400/20 text-red-400 
                          transform hover:scale-110 transition-all duration-300'
                              >
                                <Trash2 className='h-4 w-4' />
                              </button>
                            </div>
                          </td>
                        )}
                        {!isEmployee && (
                          <td className='p-2'>
                            <button
                              type='button'
                              onClick={e => {
                                e.stopPropagation()
                                openResetPasswordModal(user)
                              }}
                              className='bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded'
                            >
                              Reset Password
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr className='animate-fade-in'>
                      <td colSpan='7' className='py-10'>
                        <div className='flex flex-col items-center justify-center space-y-4'>
                          <div className='relative'>
                            <div
                              className={`absolute -inset-1 rounded-full ${theme.tailwind.bg1} animate-pulse`}
                            ></div>
                            <div
                              className={`relative flex items-center justify-center w-16 h-16 rounded-full ${theme.tailwind.bgr7} shadow-lg`}
                            >
                              <svg
                                className='w-8 h-8 text-white'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                                xmlns='http://www.w3.org/2000/svg'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth='2'
                                  d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                ></path>
                              </svg>
                            </div>
                          </div>
                          <p
                            className={`text-xl font-semibold ${theme.tailwind.bgr7} bg-clip-text text-transparent`}
                          >
                            No investors found
                          </p>
                          <p
                            className={`${theme.tailwind.text19} text-sm max-w-md text-center`}
                          >
                            Try adjusting your search criteria or add a new
                            investor to get started.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {isModalOpen && selectedEmployee && (
          <div
            className={`fixed inset-x-0 -top-16 bottom-0 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center z-50`}
          >
            <div
              className={`relative group ${gradient} p-8 rounded-3xl 
                border border-white/10 shadow-2xl backdrop-blur-xl w-full max-w-2xl transform 
                transition-all duration-500 hover:scale-[1.02] hover:border-white/20`}
            >
              <div
                className={`absolute inset-0 rounded-3xl ${theme.tailwind.bgr20} 
                  opacity-0 group-hover:opacity-100 transition-all duration-500`}
              />
              {/* Header */}
              <h2
                className={`text-3xl font-bold mb-8 ${theme.tailwind.bgr4} 
                     text-transparent bg-clip-text text-center`}
              >
                Edit Employee Info
              </h2>
              {/* Profile Image Section */}
              <div className='flex justify-center mb-8'>
                <div className='relative group'>
                  <div
                    className={`w-32 h-32 rounded-full overflow-hidden border-4 ${theme.tailwind.border1} 
                          ${theme.tailwind.groupHover11} transition-all duration-300`}
                  >
                    <img
                      src={
                        previewImage ||
                        selectedEmployee.profile_picture ||
                        '/api/placeholder/128/128'
                      }
                      alt='Profile'
                      className='w-full h-full object-cover'
                    />
                    <div
                      className='absolute inset-0 bg-black/50 flex items-center justify-center 
                            opacity-0 group-hover:opacity-100 transition-opacity duration-300'
                    >
                      <label className='cursor-pointer'>
                        <input
                          type='file'
                          className='hidden'
                          accept='image/*'
                          onChange={handleImageChange}
                        />
                        <Upload className={`h-8 w-8 ${theme.tailwind.text9}`} />
                      </label>
                    </div>
                  </div>
                  {isUploading && (
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <div
                        className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.tailwind.border25}`}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
              <div className='space-y-6'>
                {/* First Row */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='relative group'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <User
                        className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                      />
                    </div>
                    <input
                      type='text'
                      placeholder='Employee Name'
                      value={selectedEmployee.employeeData?.name || ''}
                      onChange={e =>
                        setSelectedEmployee(prev => ({
                          ...prev,
                          employeeData: {
                            ...prev.employeeData,
                            name: e.target.value
                          }
                        }))
                      }
                      className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                          ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                          ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                    />
                  </div>

                  <div className='relative group'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <IdCard
                        className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                      />
                    </div>
                    <input
                      type='text'
                      placeholder='Phone'
                      value={
                        selectedEmployee.employeeData?.personalDetails?.phone ||
                        ''
                      }
                      onChange={e => handleInputChange(e, 'phone')}
                      className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                          ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                          ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                    />
                  </div>
                </div>

                {/* Second Row */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='relative group'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <Phone
                        className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                      />
                    </div>
                    <input
                      type='text'
                      placeholder='Address'
                      value={
                        selectedEmployee.employeeData?.personalDetails
                          ?.address || ''
                      }
                      onChange={e => handleInputChange(e, 'address')}
                      className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                          ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                          ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                    />
                  </div>

                  <div className='relative group'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <Mail
                        className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                      />
                    </div>
                    <input
                      type='date'
                      placeholder='Date Of Joining'
                      value={
                        selectedEmployee?.employeeData.personalDetails
                          .dateOfJoining || ''
                      }
                      onChange={e => handleInputChange(e, 'dateOfJoining')}
                      className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                          ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                          ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                    />
                  </div>
                </div>

                {/* Username Field */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div className='relative group'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <UserPlus
                        className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                      />
                    </div>
                    <input
                      type='text'
                      placeholder='Username'
                      value={selectedEmployee.username || ''}
                      onChange={e =>
                        setSelectedEmployee(prev => ({
                          ...prev,
                          username: e.target.value
                        }))
                      }
                      className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                        ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                        ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                    />
                  </div>
                  <div className='relative group'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <Mail
                        className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                      />
                    </div>
                    <input
                      type='number'
                      placeholder='Employee Salary'
                      value={
                        selectedEmployee.employeeData?.salaryDetails?.salary ||
                        ''
                      }
                      onChange={e => handleInputChange(e, 'salary')}
                      className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                          ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                          ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                    />
                  </div>
                </div>
                <div className='relative group'>
                  <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                    <IdCard
                      className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                    />
                  </div>
                  <input
                    type='text'
                    placeholder='CNIC'
                    value={
                      selectedEmployee.employeeData?.personalDetails?.cnic
                        ? formatCNIC(
                            selectedEmployee.employeeData.personalDetails.cnic
                          )
                        : ''
                    }
                    onChange={e => handleInputChange(e, 'cnic')}
                    className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className='flex space-x-4 mt-8'>
                <button
                  onClick={handleUpdate}
                  className={`flex-1 ${theme.tailwind.bgr19} text-white px-6 py-3 rounded-xl 
                     font-semibold transform hover:scale-105 transition-all duration-300 
                     hover:shadow-lg hover:shadow-teal-500/25 active:scale-95`}
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setModalOpen(false)}
                  className='flex-1 bg-gradient-to-r from-red-400 to-red-500 text-white px-6 py-3 rounded-xl 
                     font-semibold transform hover:scale-105 transition-all duration-300 
                     hover:shadow-lg hover:shadow-red-500/25 active:scale-95'
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {isResetModalOpen && selectedUserForReset && (
          <div className='fixed inset-0 z-50'>
            {/* Backdrop */}
            <div
              className={`absolute inset-0 bg-slate-900/90 backdrop-blur-sm -mt-14`}
            />
            {/* Modal Container */}
            <div className='relative h-screen overflow-y-auto'>
              <div className='min-h-screen py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center'>
                <div
                  className={`relative ${theme.tailwind.bgGradient20} 
        rounded-2xl border border-white/10 shadow-2xl w-96 p-8 space-y-6`}
                >
                  <div className='flex justify-between items-center'>
                    <h2
                      className={`text-2xl font-bold ${theme.tailwind.text9}`}
                    >
                      Reset Password
                    </h2>
                    <button
                      onClick={() => setResetModalOpen(false)}
                      className='text-red-400 hover:text-red-300'
                    >
                      <XCircle className='w-6 h-6' />
                    </button>
                  </div>

                  <div className='space-y-4'>
                    <div className='relative'>
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        autoComplete='new-password'
                        placeholder='New Password'
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        className={`w-full p-3 pr-12 rounded-xl bg-white/5 border border-white/10 
                ${theme.tailwind.text9} ${theme.tailwind.placeHolder} ${theme.tailwind.focus}`}
                      />
                      <button
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className='absolute right-3 top-1/2 -translate-y-1/2'
                      >
                        {showNewPassword ? (
                          <EyeOff className={`${theme.tailwind.text9}`} />
                        ) : (
                          <Eye className={`${theme.tailwind.text9}`} />
                        )}
                      </button>
                    </div>

                    <div className='relative'>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete='new-password'
                        placeholder='Confirm Password'
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        className={`w-full p-3 pr-12 rounded-xl bg-white/5 border border-white/10 
                ${theme.tailwind.text9} ${theme.tailwind.placeHolder} ${theme.tailwind.focus}`}
                      />
                      <button
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className='absolute right-3 top-1/2 -translate-y-1/2'
                      >
                        {showConfirmPassword ? (
                          <EyeOff className={`${theme.tailwind.text9}`} />
                        ) : (
                          <Eye className={`${theme.tailwind.text9}`} />
                        )}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    <div
                      className={`h-2 w-full ${theme.tailwind.bg26} rounded-full overflow-hidden`}
                    >
                      <div
                        className={`h-full transition-all duration-300 ${
                          getPasswordStrength(newPassword) === 'Weak'
                            ? 'bg-red-500 w-1/3'
                            : getPasswordStrength(newPassword) === 'Medium'
                            ? 'bg-yellow-500 w-2/3'
                            : 'bg-green-500 w-full'
                        }`}
                      />
                    </div>
                    <div className={`text-sm ${theme.tailwind.text4}`}>
                      Password Strength: {getPasswordStrength(newPassword)}
                    </div>
                  </div>

                  <button
                    onClick={handleResetPassword}
                    disabled={
                      newPassword !== confirmPassword || newPassword.length < 6
                    }
                    className={`w-full py-3 rounded-xl transition-all duration-300 ${
                      newPassword !== confirmPassword || newPassword.length < 6
                        ? `bg-gray-600 text-gray-400 cursor-not-allowed`
                        : `${theme.tailwind.bgr28} text-white hover:scale-[1.02]`
                    }`}
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            </div>{' '}
          </div>
        )}
      </div>
    </div>
  )
}

export default EmployeeTable
